// © Microsoft Corporation. All rights reserved.

import { logger } from "../logging";

/**
 * Denotes the tracing fidelity for the app perf trace.
 * This is specified via the `expPerfLevel=0|1|2` parameter.
 */
export enum AppTraceLevel {
    /** The default tracing level */
    default = 0,
    /** One level higher than the default tracing level */
    medium = 1,
    /** Two levels higher than the default tracing level */
    high = 2
}

/**
 * String to boolean dictionary type definition
 */
export type BooleanMap = { [key: string]: boolean };

export interface AppPerfTraceConfig {
    /**
     * Flag to indicate what we're tracking all experiences.
     */
    trackAllExp?: boolean;

    /**
     * Top-level flag to enable perf timeline features.
     */
    enablePerfTimeline?: boolean;

    /**
     * In case we're excluding specific experiences from tracking. Useful when
     * we want to track all experiences, except for a few noisy ones, for example.
     */
    experiencesForPerf?: BooleanMap;

    /**
     * In case we're excluding specific experiences from tracking. Useful when
     * we want to track all experiences, except for a few noisy ones, for example.
     */
    experiencesExcludedForPerf?: BooleanMap;

    /**
     * Flag to enable performance marks in the F12 timeline.
     */
    addPerformanceMarks?: boolean;

    /**
     * Flag to enable adding the page measurements to the _pageTimings object.
     */
    addPageTimings?: boolean;

    /**
     * Swtich to determine if we will upload the trace or not using the telemetry
     * API.
     */
    uploadTrace?: boolean;

    /**
     * Specify interval after TTVR at when to start the app trace calculation & upload process
     */
    uploadTraceDelay?: number;

    /**
     * The sampling rate at which we are going to collect this data from users
     */
    sampleRate?: number;

    /**
     * The fidelity of the trace that should be captured. This value can be used by custom instrumentation
     * sites to emit higher fidelity instrumentation (potentially at the cost of additional perf hit).
     * The use case for this value will vary by the specific instrumentation implementation/scenario.
     */
    traceLevel?: AppTraceLevel;

    /**
     * The collection of upload criteria that may trigger app trace upload.
     * Meeting any of these criteria can trigger app trace upload.
     */
    uploadCriteria?: {

        /**
         * Optional minimum TTVR for which we are going to trigger app trace upload.
         */
        minimumTTVR?: number;

        /**
         * Optional list of app error codes for which app trace upload may be triggerred.
         */
        uploadOnErrorCodes?: Array<number>;
    };
}

/**
 * Parses app perf diagnostic configuration from the given URL.
 * @param locationHref the value of window.location.href form which to parse options.
 */
export function tryParseAppTraceConfig(locationHref: string): Partial<AppPerfTraceConfig> {
    try {
        const searchParams = (new URL(locationHref)).searchParams;
        const expPerfQsp = searchParams.get("expPerf");

        // if we were explicitly asked to skip everything, return early with
        // explicitly set disabled flag so this override will supercede everything.
        const expPerfSkipQsp = searchParams.get("expPerfSkip");
        if (expPerfSkipQsp == "all") {
            return {
                enablePerfTimeline: false
            };
        }

        // If nothing explicit was requested, return early with empty config,
        // so any config level overrides can continue to get applied.
        if (!expPerfSkipQsp && !expPerfQsp) {
            return {};
        }

        const parsedConfig: Partial<AppPerfTraceConfig> = {
            enablePerfTimeline: true,

            // since we know that someone is trying to collect data via QSPs,
            // we explicitly set few properties so that we override any base config values.
            uploadTrace: false,
            sampleRate: 1,
            uploadCriteria: {
                minimumTTVR: 0
            }
        };

        if (expPerfQsp === "all") {

            parsedConfig.trackAllExp = true;
            parsedConfig.experiencesExcludedForPerf = {};

        } else if (expPerfQsp) {

            // If an explicit experience name list was specified, convert it to a boolean map
            // for efficient lookup so that we can create or avoid creation of the timeline object
            // for whatever experience needs instrumentation.
            const experiencesForPerf = parsedConfig.experiencesForPerf = {};
            expPerfQsp.split(",").forEach(expName =>
                experiencesForPerf[expName] = true
            );
        }

        // Parse explicit exclusions from perf timeline
        if (expPerfSkipQsp) {
            const experiencesExcludedForPerf = parsedConfig.experiencesExcludedForPerf = {};
            expPerfSkipQsp.split(",").forEach(expName =>
                experiencesExcludedForPerf[expName] = true
            );
        }

        if (searchParams.get("expPerfMarks") === "true") {
            parsedConfig.addPerformanceMarks = true;
        }

        if (searchParams.get("expPerfTimings") === "true") {
            parsedConfig.addPageTimings = true;
        }

        const expPerfLevel = parseInt(searchParams.get("expPerfLevel"));
        parsedConfig.traceLevel = AppTraceLevel[expPerfLevel] != undefined
            ? expPerfLevel
            : AppTraceLevel.default;

        return parsedConfig;

    } catch (error) {

        logger.logError("Error parsing URL for detecting experience diagnostic switches: " + error);
        return {};
    }
}